import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Trash2, Calendar, Archive, MapPin, History } from "lucide-react";

export function DeleteConfirmModal({ 
  deleteConfirm, 
  onClose, 
  onDeleteContact, 
  onSaveAndArchive,
  onDeleteStop,
  onDeleteHistory,
  onRemoveHistoryStop,
}) {
  const [routeDate, setRouteDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  if (!deleteConfirm) return null;

  const renderContent = () => {
    switch (deleteConfirm.type) {
      case 'batch-completed':
        return (
          <>
            <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mx-auto mb-4">
              <Archive className="w-6 h-6 text-indigo-600" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Save Route to History?</h3>
            <p className="text-slate-600 mb-4">
              Archive <span className="font-bold text-indigo-600">{deleteConfirm.count}</span> completed stop{deleteConfirm.count > 1 ? 's' : ''} with notes. Choose the date for this route:
            </p>
            <div className="mb-5">
              <div className="flex items-center gap-2 justify-center">
                <Calendar className="w-4 h-4 text-slate-500" />
                <Input
                  type="date"
                  data-testid="route-date-input"
                  value={routeDate}
                  onChange={(e) => setRouteDate(e.target.value)}
                  className="w-auto text-center font-medium"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
              <Button
                data-testid="confirm-save-history-btn"
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
                onClick={() => onSaveAndArchive(routeDate)}
              >
                <Archive className="w-4 h-4 mr-1" />Save & Archive
              </Button>
            </div>
          </>
        );

      case 'stop':
        return (
          <>
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <MapPin className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Delete Stop?</h3>
            <p className="text-slate-600 mb-1">Are you sure you want to remove this stop?</p>
            <p className="text-slate-900 font-semibold mb-4">
              {deleteConfirm.name} ({deleteConfirm.postcode})
            </p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
              <Button
                variant="destructive"
                data-testid="confirm-delete-stop-btn"
                className="flex-1 bg-red-600 hover:bg-red-700"
                onClick={() => onDeleteStop(deleteConfirm.id)}
              >
                Delete
              </Button>
            </div>
          </>
        );

      case 'history':
        return (
          <>
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <History className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Delete History Entry?</h3>
            <p className="text-slate-600 mb-1">Are you sure you want to delete this route history?</p>
            <p className="text-slate-900 font-semibold mb-4">{deleteConfirm.label}</p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
              <Button
                variant="destructive"
                data-testid="confirm-delete-history-btn"
                className="flex-1 bg-red-600 hover:bg-red-700"
                onClick={() => onDeleteHistory(deleteConfirm.id)}
              >
                Delete
              </Button>
            </div>
          </>
        );

      case 'history-stop':
        return (
          <>
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <MapPin className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Remove Stop from History?</h3>
            <p className="text-slate-600 mb-1">Are you sure you want to remove this stop from the route?</p>
            <p className="text-slate-900 font-semibold mb-4">
              {deleteConfirm.name} ({deleteConfirm.postcode})
            </p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
              <Button
                variant="destructive"
                data-testid="confirm-remove-history-stop-btn"
                className="flex-1 bg-red-600 hover:bg-red-700"
                onClick={() => onRemoveHistoryStop(deleteConfirm.entryId, deleteConfirm.stopIndex)}
              >
                Remove
              </Button>
            </div>
          </>
        );

      default: // 'contact'
        return (
          <>
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Delete Contact?</h3>
            <p className="text-slate-600 mb-1">Are you sure you want to delete this contact?</p>
            <p className="text-slate-900 font-semibold mb-4">
              {deleteConfirm.name} ({deleteConfirm.postcode})
            </p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
              <Button
                variant="destructive"
                data-testid="confirm-delete-contact-btn"
                className="flex-1 bg-red-600 hover:bg-red-700"
                onClick={() => onDeleteContact(deleteConfirm.id)}
              >
                Delete
              </Button>
            </div>
          </>
        );
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-xl p-6 max-w-sm w-full shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="text-center">
            {renderContent()}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
